/**
 * Venda na Rota — etapa 2, Produtos da carga: o vendedor só vende o que está na própria carga.
 * Cada produto mostra quanto ainda resta (carregado − vendido − o que já está nesta venda − as
 * vendas guardadas no aparelho). Pesquisa por código, descrição ou código de barras; painel de
 * inclusão com unidade, quantidade (− / +), preço (tabela do cliente), desconto e total.
 * Venda acima do saldo da carga é bloqueada (a mesma regra do servidor).
 */
import {
  formatMoney,
  formatQuantity,
  parseDecimalBR,
  toFormMoney,
  toFormNumber,
  type Product,
  type ProductListRow,
} from "@cafegestor/shared";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Field, Icon, Input, SearchInput, Select } from "@cafegestor/ui";
import { stepQuantity } from "./step-quantity";
import { newIdempotencyKey, usePriceLookup } from "@cafegestor/client";
import { useCached } from "./rota-data";
import {
  availableBase,
  lineTotal,
  quantityProblem,
  type CartLine,
  type LoadBalance,
  type PendingItems,
} from "./rota-lib";

type Draft = {
  key: string | null;
  product: Pick<ProductListRow, "id" | "code" | "name" | "baseUnitId" | "baseUnitCode">;
  unitId: string;
  quantity: string;
  unitPrice: string;
  discount: string;
};

export function ProductsStep({
  companyId,
  customerId,
  online,
  balances,
  products,
  lines,
  pending,
  onLines,
  onEditing,
}: {
  companyId: string | null;
  customerId: string;
  online: boolean;
  balances: LoadBalance[];
  /** produtos da empresa (código de barras e unidade-base) */
  products: ProductListRow[];
  lines: CartLine[];
  pending: PendingItems;
  onLines: (lines: CartLine[]) => void;
  /** avisa quando o painel de inclusão abre/fecha (a tela esconde o rodapé da venda) */
  onEditing?: (editing: boolean) => void;
}) {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const editing = draft !== null;
  useEffect(() => onEditing?.(editing), [editing, onEditing]);
  const [added, setAdded] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!added) return;
    const id = setTimeout(() => setAdded(null), 2500);
    return () => clearTimeout(id);
  }, [added]);

  const t = q.trim().toLowerCase();
  const inLoad = balances
    .filter((b) => Number(b.loaded) > 0)
    .filter((b) => {
      if (!t) return true;
      const p = products.find((x) => x.id === b.productId);
      return (
        b.productCode.toLowerCase().includes(t) ||
        b.productName.toLowerCase().includes(t) ||
        (p?.gtin ?? "") === q.trim()
      );
    });

  function open(b: LoadBalance) {
    const p = products.find((x) => x.id === b.productId);
    setDraft({
      key: null,
      product: {
        id: b.productId,
        code: b.productCode,
        name: b.productName,
        baseUnitId: p?.baseUnitId ?? "",
        baseUnitCode: b.baseUnitCode,
      },
      unitId: p?.baseUnitId ?? "",
      quantity: "1",
      unitPrice: "",
      discount: "",
    });
  }

  function edit(l: CartLine) {
    setDraft({
      key: l.key,
      product: {
        id: l.productId,
        code: l.productCode,
        name: l.productName,
        baseUnitId: products.find((x) => x.id === l.productId)?.baseUnitId ?? l.unitId,
        baseUnitCode: l.baseUnitCode,
      },
      unitId: l.unitId,
      quantity: toFormNumber(l.quantity),
      unitPrice: l.unitPrice ? toFormMoney(l.unitPrice) : "",
      discount: Number(l.discount) > 0 ? toFormMoney(l.discount) : "",
    });
  }

  if (draft) {
    return (
      <ProductEditor
        companyId={companyId}
        customerId={customerId}
        online={online}
        draft={draft}
        balances={balances}
        lines={lines}
        pending={pending}
        onCancel={() => setDraft(null)}
        onSave={(line) => {
          const exists = lines.some((l) => l.key === line.key);
          onLines(exists ? lines.map((l) => (l.key === line.key ? line : l)) : [...lines, line]);
          setAdded(
            `${exists ? "Alterado" : "Incluído"}: ${line.productCode}, ${formatQuantity(line.quantity)} ${line.unitCode}`,
          );
          setDraft(null);
          setQ("");
          // de volta à pesquisa para o próximo produto
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {added && (
        <p
          role="status"
          className="flex items-center gap-2 rounded-xl bg-success-soft px-3 py-2 text-sm font-medium text-success-strong"
        >
          <Icon name="check" size={18} strokeWidth={2.4} /> {added}
        </p>
      )}
      <SearchInput
        className="w-full"
        inputClassName="h-12 text-base"
        inputRef={searchRef}
        value={q}
        onChange={setQ}
        label="Pesquisar produto da carga"
        placeholder="Código, descrição ou código de barras"
      />
      {balances.length === 0 ? (
        <p className="rounded-xl border border-dashed border-input px-4 py-5 text-center text-sm text-muted-foreground">
          Sem produtos nesta carga
          {online ? "" : " (ou a carga ainda não foi baixada neste aparelho)"}.
        </p>
      ) : inLoad.length === 0 ? (
        <p className="rounded-xl border border-dashed border-input px-4 py-5 text-center text-sm text-muted-foreground">
          Nenhum produto da carga com “{q}”.
        </p>
      ) : (
        <ul aria-label="Produtos da carga" className="space-y-2">
          {inLoad.map((b) => {
            const left = availableBase(b.productId, balances, lines, pending);
            const empty = Number(left) <= 0;
            return (
              <li key={b.productId}>
                <button
                  type="button"
                  disabled={empty}
                  onClick={() => open(b)}
                  className="flex min-h-14 w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-left hover:border-primary/40 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold leading-snug break-words">
                      {b.productName}
                    </span>
                    <span className="block text-[13px] text-muted-foreground">
                      {b.productCode} · carregado {formatQuantity(b.loaded)} · vendido{" "}
                      {formatQuantity(b.sold)}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-[13px] font-semibold tabular-nums ${
                      empty
                        ? "bg-muted text-muted-foreground"
                        : "bg-success-soft text-success-strong"
                    }`}
                  >
                    {empty ? "Esgotado" : `Restam ${formatQuantity(left)} ${b.baseUnitCode}`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <section aria-label="Itens da venda" className="space-y-2">
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <Icon name="cart" size={18} /> Itens da venda ({lines.length})
        </h3>
        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">Toque num produto acima para incluir.</p>
        ) : (
          <ul className="space-y-2">
            {lines.map((l) => {
              const total = lineTotal(l);
              const price = l.unitPrice ?? l.listPrice;
              return (
                <li
                  key={l.key}
                  aria-label={`${l.productCode} ${l.productName}`}
                  className="rounded-xl border border-border bg-card p-3"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold leading-snug break-words">{l.productName}</p>
                      <p className="text-[13px] text-muted-foreground tabular-nums">
                        {l.productCode} · {formatQuantity(l.quantity)} {l.unitCode} ×{" "}
                        {price === null ? "preço da tabela" : formatMoney(price)}
                        {Number(l.discount) > 0 ? ` · desconto ${formatMoney(l.discount)}` : ""}
                      </p>
                    </div>
                    <p className="shrink-0 text-right font-bold tabular-nums">
                      {total === null ? "—" : formatMoney(total)}
                    </p>
                  </div>
                  <div className="mt-2 flex justify-end gap-1">
                    <button
                      type="button"
                      aria-label={`Editar ${l.productCode}`}
                      onClick={() => edit(l)}
                      className="flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Icon name="edit" size={19} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remover ${l.productCode}`}
                      onClick={() => onLines(lines.filter((x) => x.key !== l.key))}
                      className="flex size-11 items-center justify-center rounded-lg text-destructive hover:bg-destructive-soft"
                    >
                      <Icon name="trash" size={19} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function ProductEditor({
  companyId,
  customerId,
  online,
  draft,
  balances,
  lines,
  pending,
  onCancel,
  onSave,
}: {
  companyId: string | null;
  customerId: string;
  online: boolean;
  draft: Draft;
  balances: LoadBalance[];
  lines: CartLine[];
  pending: PendingItems;
  onCancel: () => void;
  onSave: (line: CartLine) => void;
}) {
  const [d, setD] = useState(draft);
  const [tried, setTried] = useState(false);
  const detail = useCached<Product>(
    companyId,
    `produto.${d.product.id}`,
    `/cadastros/produtos/${d.product.id}`,
  );
  const baseUnitId = detail.data?.baseUnitId ?? d.product.baseUnitId;
  const unitId = d.unitId || baseUnitId;
  const units = [
    { id: baseUnitId, code: d.product.baseUnitCode, factor: "1" },
    ...(detail.data?.packagings ?? [])
      .filter((p) => p.isSaleUnit && p.status === "active")
      .map((p) => ({ id: p.unitId, code: p.unitCode, factor: p.factor })),
  ].filter((u) => u.id);
  const unit = units.find((u) => u.id === unitId) ?? units[0];
  const price = usePriceLookup({
    customerId: online ? customerId : "",
    productId: d.product.id,
    unitId: unit?.id ?? "",
    warehouseId: "",
  });
  const listPrice = price.data?.listPrice ?? null;

  const quantity = parseDecimalBR(d.quantity, "numero");
  const unitPrice = d.unitPrice.trim() === "" ? null : parseDecimalBR(d.unitPrice, "dinheiro");
  const discount = d.discount.trim() === "" ? "0" : parseDecimalBR(d.discount, "dinheiro");
  const line: CartLine | null =
    quantity !== null &&
    unit &&
    discount !== null &&
    (d.unitPrice.trim() === "" || unitPrice !== null)
      ? {
          key: d.key ?? newIdempotencyKey(),
          productId: d.product.id,
          productCode: d.product.code,
          productName: d.product.name,
          unitId: unit.id,
          unitCode: unit.code,
          factor: unit.factor,
          baseUnitCode: d.product.baseUnitCode,
          quantity,
          unitPrice,
          listPrice,
          discount,
        }
      : null;
  const available = availableBase(d.product.id, balances, lines, pending, d.key);
  const problem = !unit
    ? "Carregando os dados do produto (na primeira vez, precisa de conexão)."
    : quantity === null
      ? "Quantidade inválida. Exemplo: 10 ou 2,5."
      : d.unitPrice.trim() !== "" && unitPrice === null
        ? "Preço inválido. Exemplo: 12,50."
        : discount === null
          ? "Desconto inválido. Exemplo: 1,00."
          : line
            ? quantityProblem(line, available)
            : null;
  const total = line ? lineTotal(line) : null;
  const maxPct = price.data?.maxDiscountPercent ?? null;

  return (
    <section
      aria-label={`Incluir ${d.product.code}`}
      className="space-y-4 rounded-2xl border-2 border-primary/30 bg-card p-4 shadow-card"
    >
      <div>
        <p className="text-[17px] font-semibold leading-snug break-words">{d.product.name}</p>
        <p className="text-[13px] text-muted-foreground">
          {d.product.code} · restam {formatQuantity(Number(available) > 0 ? available : "0")}{" "}
          {d.product.baseUnitCode} na carga
        </p>
      </div>
      {units.length > 1 && (
        <Field label="Unidade">
          <Select
            className="h-12"
            value={unit?.id ?? ""}
            onChange={(e) => setD({ ...d, unitId: e.target.value, unitPrice: "" })}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
                {u.factor !== "1" ? ` (${formatQuantity(u.factor)} ${d.product.baseUnitCode})` : ""}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div>
        <p id="qtd-label" className="mb-1.5 text-sm font-medium">
          Quantidade{unit ? ` (${unit.code})` : ""}
        </p>
        <div className="grid grid-cols-[3.25rem_1fr_3.25rem] items-center gap-2">
          <button
            type="button"
            aria-label="Diminuir quantidade"
            onClick={() => setD({ ...d, quantity: stepQuantity(d.quantity, -1) })}
            className="flex size-13 h-13 items-center justify-center rounded-xl border border-input text-primary"
          >
            <Icon name="minus" size={22} strokeWidth={2.4} />
          </button>
          <Input
            aria-labelledby="qtd-label"
            inputMode="decimal"
            enterKeyHint="done"
            className="h-13 text-center text-xl font-semibold tabular-nums"
            value={d.quantity}
            onChange={(e) => setD({ ...d, quantity: e.target.value })}
            {...(tried && problem ? { "aria-invalid": true } : {})}
          />
          <button
            type="button"
            aria-label="Aumentar quantidade"
            onClick={() => setD({ ...d, quantity: stepQuantity(d.quantity, 1) })}
            className="flex h-13 items-center justify-center rounded-xl bg-primary text-primary-foreground"
          >
            <Icon name="plus" size={22} strokeWidth={2.4} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Preço unit. (R$)"
          hint={
            listPrice
              ? `Tabela: ${formatMoney(listPrice)}`
              : online
                ? price.isFetching
                  ? "Consultando a tabela…"
                  : "Fora da tabela: informe"
                : "Em branco: preço da tabela"
          }
        >
          <Input
            inputMode="decimal"
            className="h-12 tabular-nums"
            placeholder={listPrice ? toFormMoney(listPrice) : "tabela"}
            value={d.unitPrice}
            onChange={(e) => setD({ ...d, unitPrice: e.target.value })}
          />
        </Field>
        <Field
          label="Desconto (R$)"
          hint={maxPct ? `Até ${toFormNumber(maxPct)}% sem liberação` : undefined}
        >
          <Input
            inputMode="decimal"
            className="h-12 tabular-nums"
            placeholder="0,00"
            value={d.discount}
            onChange={(e) => setD({ ...d, discount: e.target.value })}
          />
        </Field>
      </div>
      <div className="flex items-baseline justify-between rounded-xl bg-caramel-soft px-4 py-3">
        <span className="text-sm font-medium text-primary">Total do item</span>
        <span className="text-xl font-bold tabular-nums text-primary">
          {total === null ? "—" : formatMoney(total)}
        </span>
      </div>
      {tried && problem && <Alert tone="error">{problem}</Alert>}
      {!tried && line && problem && <p className="text-sm text-destructive">{problem}</p>}
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <Button variant="secondary" size="lg" onClick={onCancel}>
          Voltar
        </Button>
        <Button
          size="lg"
          onClick={() => {
            setTried(true);
            if (!problem && line) onSave(line);
          }}
        >
          <Icon name={d.key ? "check" : "plus"} size={20} strokeWidth={2.4} />
          {d.key ? "Salvar alteração" : "Adicionar produto"}
        </Button>
      </div>
    </section>
  );
}
